<template>
  <RevCard class="p-24">
    <Items :items />
    <TradeIn :swap-offer :with-swap="Boolean(swapOffer)" />

    <RevDivider class="my-16" />

    <SubTotals
      :discount-price="props.discount.deductionWithCurrency"
      :discount-token
      :flash-sales-discount
      :service-fee="summary.serviceFee"
      :subtotal="summary.totalGrossPriceWithoutBmFee"
      :tax="summary.tax"
      :with-tax="shouldDisplayTax"
    />

    <Totals
      :items
      :price-after-discount
      :selected-payment-method
      :total-price-after-buyback="summary.totalPriceAfterBuyback"
      :with-swap="Boolean(swapOffer)"
      :with-tax="shouldDisplayTax"
    />

    <div id="checkout-side-cart-submit" />

    <p
      v-if="withMonthlyPayment"
      class="text-static-default-low caption mb-8 mt-16"
    >
      {{ i18n(translations.recurring) }}
    </p>

    <div class="space-y-24">
      <CheckoutLegalLinks
        :with-cancel="withExtendedLegal"
        :with-swap="Boolean(swapOffer)"
      />

      <PaymentMethodsWrapper v-bind="paymentMethodsProps" />
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { BuybackOffer } from '@backmarket/http-api'
import type { PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import type {
  CartItem,
  CartSummary,
} from '@backmarket/nuxt-layer-checkout/api/cart/types'
import CheckoutLegalLinks from '@backmarket/nuxt-layer-checkout/components/CheckoutLegalLinks/CheckoutLegalLinks.vue'
import { DEFAULT_INSURANCE_ID } from '@backmarket/nuxt-layer-checkout/constants'
import type { PaymentMethodsFeatures } from '@backmarket/nuxt-layer-payment/methods'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevCard } from '@ds/components/Card'
import { RevDivider } from '@ds/components/Divider'

import type { Discount } from '~/scopes/checkout/types/discount'
import { getFlashSalesDiscount } from '~/scopes/checkout/utils/flash-sales'
import { getPriceAfterDiscount } from '~/scopes/checkout/utils/getPriceAfterDiscount'
import { hasInsuranceSelected } from '~/scopes/insurance/utils/insuranceOffers'

import translations from './CheckoutSummaryContent.translations'
import Items from './components/Items/Items.vue'
import PaymentMethodsWrapper from './components/PaymentMethodsWrapper/PaymentMethodsWrapper.vue'
import SubTotals from './components/SubTotals/SubTotals.vue'
import Totals from './components/Totals/Totals.vue'
import TradeIn from './components/TradeIn/TradeIn.vue'

const i18n = useI18n()

const props = defineProps<{
  discount: Discount
  discountToken: string
  summary: CartSummary
  items: CartItem[]
  selectedPaymentMethod?: PaymentMethod | null
  swapOffer?: BuybackOffer | null
  withExtendedLegal: boolean
  withPaymentAvailability: boolean
  methodsFeatures?: PaymentMethodsFeatures
}>()

const flashSalesDiscount = computed(() => getFlashSalesDiscount(props.items))

const withMonthlyPayment = computed<boolean>(() => {
  return props.items.some((item) =>
    item.insuranceOffers?.some(
      (offer) =>
        offer.selected &&
        offer.id.toString() !== DEFAULT_INSURANCE_ID &&
        offer.isMonthly,
    ),
  )
})

const priceAfterDiscount = computed(() =>
  getPriceAfterDiscount(props.discount, props.summary),
)

const paymentMethodsProps = computed(() => ({
  withPaymentAvailability: props.withPaymentAvailability,
  listings: props.items.map((item) => item.listingId),
  bagPrice: priceAfterDiscount.value,
  hasInsurance: props.items.some((item) =>
    hasInsuranceSelected(item.insuranceOffers),
  ),
  tokenization: withMonthlyPayment.value,
  features: props.methodsFeatures,
}))

const shouldDisplayTax = computed(
  () => !props.summary.totalPriceIncludesTax || !isEmpty(props.summary.tax),
)
</script>
